import {
  Component,
  HostListener,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpResponse } from '@angular/common/http';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';
import { STATIC_DATA, STATIC_JSON_DATA_PATH } from '../../../../shared/constants/static-data.constants';
import { ListTableComponent } from '../../../../shared/components/list-table/list-table.component';
import { PageConfig } from '../../../../shared/authorization/auth.config';
import { Router } from '@angular/router';
import { ClaimsService } from '../../../../services/claims.service';
import { SectionsService } from '../../../../services/sections.service';
import { AuthorizationUtility } from '../../../../shared/authorization/auth.utility';
import { AuthorizationService } from '../../../../shared/authorization/authorization.service';
import { SharedDataService } from '../../../../shared/services/shared-data-service';
import { BillingService } from '../../../../services/billing.service';
import {
  DialogConfig,
  DialogService,
} from '../../../../shared/services/dialog.service';
import { BillingFilterComponent } from '../../billing-filter/billing-filter.component';
import { ListService } from '../../../../shared/services/list.service';
import { IgnoredTimesheetsComponent } from '../../ignored-timesheets/ignored-timesheets/ignored-timesheets.component';
import { UserService } from '../../../../services/user.service';
import { LookupService } from '../../../../services/lookup.service';
import { LoggerService } from '../../../../shared/services/logger.service';
import { AccordionComponent } from '../../../../shared/components/accordion/accordion.component';
import { PopupDialogComponent } from '../../../../shared/components/popup-dialog/popup-dialog.component';
import { DropdownSelectComponent } from '../../../../shared/components/dropdown-select/dropdown-select.component';

interface CashClaimRow {
  tasBillingBatchClaimDetailId: number;
  icn: string;
  consumer: string;
  serviceDate: string;
  claimAmount: number;
  paid: number;
  outstanding: number;
  payment: number;
  carc: string;
  rarc: string;
}

interface AddDepositErrors {
  checkNumber?: string;
  depositAmount?: string;
  mcoOrgName?: string;
  bankAccount?: string;
  depositDate?: string;
}

@Component({
  selector: 'ppl-deposit-entry-list',
  standalone: true,
  imports: [
    AccordionComponent,
    IgnoredTimesheetsComponent,
    ListTableComponent,
    PopupDialogComponent,
    DropdownSelectComponent,
    FormsModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzDatePickerModule,
    NzCheckboxModule,
    AutoCompleteModule,
  ],
  templateUrl: './deposit-entry-list.component.html',
  styleUrl: './deposit-entry-list.component.css',
})
export class DepositEntryListComponent implements OnInit {
  sharedDataKey: string = STATIC_DATA.SHARED_BATCHES_DATA;
  pageId: string = PAGE_CONSTANTS.BATCHES_GRID_PAGE;
  programName!: string;
  lazyLoad = true;

  cols = [
    { field: 'depositId', header: 'Deposit ID' },
    { field: 'depositDate', header: 'Deposit Date' },
    { field: 'checkNumber', header: 'Check Number' },
    { field: 'depositAmount', header: 'Deposit Amount' },
    { field: 'mcoOrgName', header: 'MCO' },
    { field: 'bankName', header: 'Bank Name' },
    { field: 'accountNumber', header: 'Account Number' },
    { field: 'utilizationPct', header: 'Utilization %' },
    { field: 'isAdvanceDepositPill', header: 'Is Advance Deposit' },
  ];

  manualCashButtonConfig = [
    {
      key: 'applyManualCash',
      icon: 'fa fa-money',
      label: () => 'Apply Manual Cash',
      disabled: () => false,
      condition: () => true,
    },
  ];

  deposits: any[] = [];

  tileCount = 0;
  tileTotal = '$0.00';
  tileApplied = '$0.00';
  tileUnapplied = '$0.00';

  private tileAppliedRaw = 0;
  private tileUnappliedRaw = 0;

  filterCheckNumber = '';
  filterMco = '';
  filterAccountNumber = '';
  filterIsAdvanceDeposit = ''; // '', 'true', 'false'
  filterDepositDateFrom: Date | null = null;
  filterDepositDateTo: Date | null = null;
  filterUtilizationStatus = ''; // '', 'UNDER_UTILIZED', 'OVER_UTILIZED', 'FULLY_UTILIZED'
  utilizationStatusOptions = [
    { label: 'Under Utilized', value: 'UNDER_UTILIZED' },
    { label: 'Over Utilized', value: 'OVER_UTILIZED' },
    { label: 'Fully Utilized', value: 'FULLY_UTILIZED' },
  ];

  bankAccounts: any[] = [];
  mcoOptions: string[] = [];

  bankAccountOptionsForAdd: any[] = [];
  filterMcoOptions: string[] = [];
  filterAccountOptions: string[] = [];
  filteredAccountOptions: string[] = [];

  newDeposit: {
    checkNumber: string;
    depositAmount: string;
    mcoOrgName: string;
    accountNumber: string;
    isAdvanceDeposit: boolean;
    depositDate: Date | null;
  } = {
    checkNumber: '',
    depositAmount: '',
    mcoOrgName: '',
    accountNumber: '',
    isAdvanceDeposit: false,
    depositDate: new Date(),
  };
  addDepositErrors: AddDepositErrors = {};
  addDepositMessage = '';
  addDepositApiError = '';
  addDepositSubmitting = false;

  // ---------------------------------------------------------------------------
  // Apply Manual Cash modal
  // ---------------------------------------------------------------------------
  showCashModal = false;
  cashDeposit: any = null;
  cashClaims: CashClaimRow[] = [];
  cashIcnForm!: FormGroup;
  cashIcnError = '';
  cashApplyError = '';
  cashLookupLoading = false;
  cashApplySubmitting = false;

  // ---------------------------------------------------------------------------
  // Export modal
  // ---------------------------------------------------------------------------
  showExportModal = false;
  exportTimeframe: 'today' | 'last30' | 'last180' = 'today';
  exportTimeframeOptions = [
    { label: 'Today', value: 'today' },
    { label: 'Last 30 Days', value: 'last30' },
    { label: 'Last 180 Days', value: 'last180' },
  ];
  exportStartDate: Date | null = null;
  exportEndDate: Date | null = null;
  exportReportType = '';
  exportReportTypeOptions = [
    { label: 'All Deposits', value: '' },
    { label: 'Missing Remittance', value: 'MISSING_REMITTANCE' },
  ];
  exportSubmitting = false;
  exportError = '';

  constructor(
    private router: Router,
    private sharedDataService: SharedDataService,
    private sectionService: SectionsService,
    private claimsService: ClaimsService,
    private billingService: BillingService,
    private listService: ListService,
    private userService: UserService,
    private logger: LoggerService,
    private fb: FormBuilder,
    private lookupService: LookupService
  ) {
    this.cashIcnForm = this.fb.group({ icn: [''] });
  }

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.loadDeposits();
    this.loadDepositSummary();
    this.loadBankAccounts();
    this.loadMcoOptions();
  }

  private loadDepositSummary(): void {
    this.billingService.getDepositSummary(this.programName || 'NY').subscribe({
      next: (response: any) => {
        const summary = response?.responsedata;
        if (!summary) {
          return;
        }
        this.tileCount = summary.depositsEnteredCount ?? 0;
        this.tileTotal = this.formatCurrency(summary.totalDepositedAmount);
        this.tileAppliedRaw = Number(summary.matchedAmount) || 0;
        this.tileUnappliedRaw = Number(summary.unmatchedAmount) || 0;
        this.tileApplied = this.formatCurrency(this.tileAppliedRaw);
        this.tileUnapplied = this.formatCurrency(this.tileUnappliedRaw);
      },
      error: (err) => {
        this.logger.error('Failed to load deposit summary', err);
      },
    });
  }

  private loadBankAccounts(): void {
    this.billingService.getDepositBankAccounts(this.programName || 'NY').subscribe({
      next: (response: any) => {
        this.bankAccounts = response?.responsedata ?? [];
        this.filterMcoOptions = Array.from(new Set(this.bankAccounts.map((b) => b.mcoOrgName))).sort();
        this.filterAccountOptions = Array.from(new Set(this.bankAccounts.map((b) => b.accountNumber))).sort();
        this.bankAccountOptionsForAdd = this.buildBankAccountOptions(this.bankAccounts);
      },
      error: (err) => {
        this.logger.error('Failed to load bank accounts', err);
      },
    });
  }

  private loadMcoOptions(): void {
    this.lookupService.getOrgData(this.programName || 'NY').subscribe({
      next: (response: any) => {
        const orgs = response?.responsedata ?? [];
        this.mcoOptions = orgs
          .filter((o: any) => o.orgTypeLookup === 'MCO')
          .map((o: any) => o.orgName)
          .sort();
      },
      error: (err: any) => {
        this.logger.error('Failed to load MCO org list', err);
      },
    });
  }

  private buildBankAccountOptions(accounts: any[]): any[] {
    return accounts.map((b) => ({ ...b, label: `${b.bankName} — ${b.accountNumber}` }));
  }

  onAddDepositMcoChange(): void {
    const mco = this.newDeposit.mcoOrgName;
    if (!mco) {
      this.bankAccountOptionsForAdd = this.buildBankAccountOptions(this.bankAccounts);
      return;
    }
    this.billingService.getDepositBankAccounts(this.programName || 'NY', { mcoOrgName: mco }).subscribe({
      next: (response: any) => {
        const accounts = response?.responsedata ?? [];
        this.bankAccountOptionsForAdd = this.buildBankAccountOptions(accounts);
        const stillValid = this.bankAccountOptionsForAdd.some((b) => b.accountNumber === this.newDeposit.accountNumber);
        if (!stillValid) {
          this.newDeposit.accountNumber = this.bankAccountOptionsForAdd.length === 1
            ? this.bankAccountOptionsForAdd[0].accountNumber
            : '';
        }
      },
      error: (err) => {
        this.logger.error('Failed to load bank accounts for MCO', err);
      },
    });
  }

  onAddDepositAccountChange(): void {
    const accountNumber = this.newDeposit.accountNumber;
    if (!accountNumber) {
      return;
    }
    this.billingService.getDepositBankAccounts(this.programName || 'NY', { accountNumber }).subscribe({
      next: (response: any) => {
        const accounts = response?.responsedata ?? [];
        const mcoNames: string[] = Array.from(new Set(accounts.map((b: any) => b.mcoOrgName as string).filter(Boolean)));
        if (mcoNames.length === 1) {
          this.newDeposit.mcoOrgName = mcoNames[0];
        }
      },
      error: (err) => {
        this.logger.error('Failed to load MCO for bank account', err);
      },
    });
  }

  onFilterSearch(): void {
    this.loadDeposits();
  }

  filterAccountNumberOptions(event: { query: string }): void {
    const query = (event.query ?? '').toLowerCase();
    this.filteredAccountOptions = this.filterAccountOptions.filter((accountNumber) =>
      accountNumber.toLowerCase().includes(query)
    );
  }

  onClearFilters(): void {
    this.filterCheckNumber = '';
    this.filterMco = '';
    this.filterAccountNumber = '';
    this.filterIsAdvanceDeposit = '';
    this.filterDepositDateFrom = null;
    this.filterDepositDateTo = null;
    this.filterUtilizationStatus = '';
    this.loadDeposits();
  }

  onGridAction(event: { action: string; data: any }): void {
    if (event?.action === 'applyManualCash') {
      this.openCashModal(event.data);
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.showCashModal) {
      this.closeCashModal();
    }
  }

  private loadDeposits(): void {
    const searchParam: { jsonPath: string; operator: string; value: string }[] = [];
    if (this.filterCheckNumber.trim()) {
      searchParam.push({ jsonPath: 'checkNumber', operator: 'equal', value: this.filterCheckNumber.trim() });
    }
    if (this.filterMco.trim()) {
      searchParam.push({ jsonPath: 'mcoOrgName', operator: 'equal', value: this.filterMco.trim() });
    }
    if (this.filterAccountNumber.trim()) {
      searchParam.push({ jsonPath: 'accountNumber', operator: 'equal', value: this.filterAccountNumber.trim() });
    }
    if (this.filterIsAdvanceDeposit) {
      searchParam.push({ jsonPath: 'isAdvanceDeposit', operator: 'equal', value: this.filterIsAdvanceDeposit });
    }
    if (this.filterUtilizationStatus) {
      searchParam.push({ jsonPath: 'utilizationStatus', operator: 'EQUAL', value: this.filterUtilizationStatus });
    }

    let orderBy = 'desc';
    if (this.filterDepositDateFrom && this.filterDepositDateTo) {
      searchParam.push({ jsonPath: 'depositDate', operator: 'GREATERTHANOREQUAL', value: this.toMmDdYyyy(this.filterDepositDateFrom) });
      searchParam.push({ jsonPath: 'depositDate', operator: 'LESSTHANOREQUAL', value: this.toMmDdYyyy(this.filterDepositDateTo) });
      orderBy = 'ASC';
    } else if (this.filterDepositDateFrom || this.filterDepositDateTo) {
      const only = (this.filterDepositDateFrom ?? this.filterDepositDateTo)!;
      searchParam.push({ jsonPath: 'depositDate', operator: 'equal', value: this.toMmDdYyyy(only) });
    }

    const payload = {
      pageName: 'billingDepositView',
      programCode: this.programName || 'NY',
      condition: 'and',
      recordsPerPage: 100,
      startingPageNumber: 1,
      orderByColumn: ['depositDate'],
      orderBy: [orderBy],
      searchParam,
    };

    this.listService.getListData(payload, 'billing').subscribe({
      next: (response: any) => {
        if (response?.responsemsg?.[0]?.code === 'INTERNAL_SERVER_ERROR') {
          return;
        }
        const rows: any[] = response?.responsedata ?? [];
        this.deposits = rows.map((d) => this.mapDepositRow(d));
      },
      error: (err) => {
        this.logger.error('Failed to load deposits', err);
        this.deposits = [];
      },
    });
  }

  private mapDepositRow(d: any) {
    const depositId = d?.tasBillingDepositDetailId ?? d?.billingDepositDetailId;
    const rawAmount = Number(d?.depositAmount ?? 0);
    const utilizationPct = d?.utilizationPercent ?? (d?.isCompletelyUtilised === true ? 100 : 0);
    const outstandingAmount = d?.outstandingAmount ?? (rawAmount * (100 - utilizationPct)) / 100;
    const isAdvance = d?.isAdvanceDeposit === true;

    return {
      rawAmount,
      rawUtilizationPct: utilizationPct,
      outstandingAmountRaw: outstandingAmount,
      billingDepositDetailId: depositId,
      mcoOrgNameRaw: d?.mcoOrgName,
      checkNumberRaw: d?.checkNumber,
      depositDateRaw: d?.depositDate,

      depositId: depositId ?? '-',
      depositDate: d?.depositDate ?? '-',
      checkNumber: d?.checkNumber ?? '-',
      depositAmount: this.formatCurrency(d?.depositAmount),
      mcoOrgName: d?.mcoOrgName ?? '-',
      bankName: d?.bankName ?? '-',
      accountNumber: d?.accountNumber ?? '-',
      utilizationPct,
      utilizationPctLabel: (Math.floor(utilizationPct * 10) / 10).toFixed(1),
      utilizationBand: utilizationPct >= 100 ? 'full' : utilizationPct > 0 ? 'part' : 'none',
      isAdvanceDepositPill: isAdvance ? 'YES' : 'NO',
      isFullyUtilized: utilizationPct >= 100,
    };
  }

  onAddDeposit(): void {
    this.addDepositErrors = {};
    this.addDepositMessage = '';
    this.addDepositApiError = '';

    const checkNumber = this.newDeposit.checkNumber.trim();
    if (!checkNumber) {
      this.addDepositErrors.checkNumber = 'Check number is required';
    } else if (!/^[A-Za-z0-9-]{1,20}$/.test(checkNumber)) {
      this.addDepositErrors.checkNumber = 'Letters, numbers and dashes only';
    }

    const rawAmount = this.newDeposit.depositAmount.trim().replace(/[$,]/g, '');
    const amount = parseFloat(rawAmount);
    if (!rawAmount) {
      this.addDepositErrors.depositAmount = 'Deposit amount is required';
    } else if (isNaN(amount) || amount < 0) {
      this.addDepositErrors.depositAmount = 'Enter an amount of 0 or greater';
    } else if (!/^\d+(\.\d{1,2})?$/.test(rawAmount)) {
      this.addDepositErrors.depositAmount = 'Up to 2 decimal places';
    }

    if (!this.newDeposit.mcoOrgName) {
      this.addDepositErrors.mcoOrgName = 'Select an MCO';
    }
    if (!this.newDeposit.accountNumber) {
      this.addDepositErrors.bankAccount = 'Select a bank account';
    }
    if (!this.newDeposit.depositDate) {
      this.addDepositErrors.depositDate = 'Deposit date is required';
    }

    if (Object.keys(this.addDepositErrors).length) {
      return;
    }

    const payload = {
      checkNumber,
      depositAmount: amount,
      mcoOrgName: this.newDeposit.mcoOrgName,
      accountNumber: this.newDeposit.accountNumber,
      depositDate: this.toIsoDate(this.newDeposit.depositDate!),
      isAdvanceDeposit: this.newDeposit.isAdvanceDeposit,
    };

    this.addDepositSubmitting = true;
    this.billingService.addBillingDeposit(this.programName || 'NY', payload).subscribe({
      next: () => {
        this.addDepositSubmitting = false;
        this.resetAddDepositFields();
        this.addDepositMessage = `Deposit for check ${checkNumber} posted · ${this.formatCurrency(amount)}`;
        this.loadDeposits();
        this.loadDepositSummary();
      },
      error: (err) => {
        this.addDepositSubmitting = false;
        this.logger.error('Failed to add deposit', err);
        this.addDepositApiError = 'Could not save this deposit. Please try again.';
      },
    });
  }

  private formatIsoDate(iso: string): string {
    if (!iso) {
      return '-';
    }
    const [year, month, day] = iso.split('-');
    return year && month && day ? `${month}/${day}/${year}` : iso;
  }

  private resetAddDepositFields(): void {
    this.newDeposit = {
      checkNumber: '',
      depositAmount: '',
      mcoOrgName: '',
      accountNumber: '',
      isAdvanceDeposit: false,
      depositDate: new Date(),
    };
    this.bankAccountOptionsForAdd = this.buildBankAccountOptions(this.bankAccounts);
    this.addDepositErrors = {};
  }

  onClearAddDepositForm(): void {
    this.resetAddDepositFields();
    this.addDepositMessage = '';
    this.addDepositApiError = '';
  }

  // ---------------------------------------------------------------------------
  // Apply Manual Cash modal
  // ---------------------------------------------------------------------------

  private cashCacheKey(depositId: any): string {
    return `depositEntry.cashClaims.${depositId}`;
  }

  openCashModal(deposit: any): void {
    this.cashDeposit = deposit;
    this.cashIcnForm.reset({ icn: '' });
    this.cashIcnError = '';
    this.cashApplyError = '';
    this.cashClaims = this.loadCashClaimsFromCache(deposit.billingDepositDetailId);
    this.refreshCashApplyError();
    this.showCashModal = true;
  }

  closeCashModal(): void {
    this.showCashModal = false;
    this.cashDeposit = null;
    this.cashClaims = [];
  }

  unappliedBalance(): number {
    return this.cashDeposit?.outstandingAmountRaw ?? 0;
  }

  totalPayment(): number {
    return this.cashClaims.reduce((sum, c) => sum + (c.payment || 0), 0);
  }

  remainingOnDeposit(): number {
    return this.unappliedBalance() - this.totalPayment();
  }

  // Enter / Add Claim clicked in the modal
  onAddCashClaim(): void {
    const icn = String(this.cashIcnForm.value.icn ?? '').trim().toUpperCase();
    this.cashIcnError = '';

    if (!icn) {
      this.cashIcnError = 'Enter a PPL ICN';
      return;
    }
    if (this.cashClaims.some((c) => c.icn === icn)) {
      this.cashIcnError = `${icn} is already on this application`;
      return;
    }

    this.cashLookupLoading = true;
    this.billingService.getClaimForManualCash(this.programName || 'NY', icn).subscribe({
      next: (response: any) => {
        this.cashLookupLoading = false;
        const claim = response?.responsedata;
        if (!claim) {
          this.cashIcnError = `No claim found for ICN ${icn}`;
          return;
        }
        const outstanding = Number(claim.outstanding);
        const remaining = this.unappliedBalance() - this.totalPayment();
        this.cashClaims.push({
          tasBillingBatchClaimDetailId: claim.tasBillingBatchClaimDetailId,
          icn: claim.pplIcn ?? icn,
          consumer: claim.consumerName,
          serviceDate: this.formatIsoDate(claim.serviceDate),
          claimAmount: Number(claim.claimAmount),
          paid: Number(claim.paid),
          outstanding,
          payment: Math.max(0, Math.min(outstanding, remaining)),
          carc: '',
          rarc: '',
        });

        this.cashIcnForm.reset({ icn: '' });
        this.saveCashClaimsToCache();
        this.refreshCashApplyError();
      },
      error: (err) => {
        this.cashLookupLoading = false;
        this.logger.error('Failed to look up claim for manual cash', err);
        this.cashIcnError = this.backendErrorMessage(err) ?? `No claim found for ICN ${icn}`;
      },
    });
  }

  private backendErrorMessage(err: any): string | null {
    const desc = err?.error?.responsemsg?.[0]?.desc;
    return typeof desc === 'string' && desc.trim() ? desc : null;
  }

  removeCashClaim(icn: string): void {
    this.cashClaims = this.cashClaims.filter((c) => c.icn !== icn);
    this.saveCashClaimsToCache();
    this.refreshCashApplyError();
  }

  onPaymentChange(claim: CashClaimRow, rawValue: string): void {
    const value = parseFloat(String(rawValue).replace(/[$,]/g, ''));
    claim.payment = isNaN(value) ? 0 : value;
    this.saveCashClaimsToCache();
    this.refreshCashApplyError();
  }

  onCarcRarcChange(): void {
    this.saveCashClaimsToCache();
  }

  isPaymentOver(claim: CashClaimRow): boolean {
    return claim.payment > claim.outstanding;
  }

  cashApplyDisabled(): boolean {
    return false;
  }

  private refreshCashApplyError(): void {
    const over = this.cashClaims.filter((c) => this.isPaymentOver(c));
    if (over.length) {
      this.cashApplyError = `Payment exceeds outstanding on ${over.map((c) => c.icn).join(', ')}`;
    } else if (this.remainingOnDeposit() < 0) {
      this.cashApplyError = `Total payment exceeds the unapplied balance on this deposit by ${this.formatCurrency(-this.remainingOnDeposit())}`;
    } else {
      this.cashApplyError = '';
    }
  }

  onApplyCash(): void {
    if (this.cashApplyDisabled() || this.cashApplySubmitting) {
      return;
    }

    const applied = this.totalPayment();
    const claimCount = this.cashClaims.length;
    const depositId = this.cashDeposit.depositId;
    const depositDetailId = this.cashDeposit.billingDepositDetailId;

    const payload = {
      tasBillingDepositDetailId: depositDetailId,
      claims: this.cashClaims.map((c) => ({
        tasBillingBatchClaimDetailId: c.tasBillingBatchClaimDetailId,
        amount: c.payment,
        primaryCarc: c.carc.trim() || null,
        primaryRarc: c.rarc.trim() || null,
      })),
    };

    this.cashApplySubmitting = true;
    this.billingService.applyBillingDepositManualCash(this.programName || 'NY', payload).subscribe({
      next: (response: any) => {
        this.cashApplySubmitting = false;
        const result = response?.responsedata;
        const isSuccess = !result?.status || String(result.status).toUpperCase() === 'SUCCESS';
        if (!isSuccess) {
          const desc = response?.responsemsg?.[0]?.desc;
          const errCode = result?.errCode;
          const hasMeaningfulErrCode = errCode !== null && errCode !== undefined && String(errCode).trim() !== '' && String(errCode) !== '0';
          this.cashApplyError = (typeof desc === 'string' && desc.trim())
            ? desc
            : hasMeaningfulErrCode ? String(errCode) : 'Failed to apply cash to these claims. Please try again.';
          return;
        }

        this.clearCashClaimsCache(depositDetailId);
        this.addDepositMessage = `${this.formatCurrency(applied)} applied to ${claimCount} ${claimCount === 1 ? 'claim' : 'claims'} on ${depositId}.`;
        this.closeCashModal();
        this.loadDeposits();
        this.loadDepositSummary();
      },
      error: (err) => {
        this.cashApplySubmitting = false;
        this.logger.error('Failed to apply manual cash', err);
        this.cashApplyError = this.backendErrorMessage(err) ?? 'Failed to apply cash to these claims. Please try again.';
      },
    });
  }

  private loadCashClaimsFromCache(depositId: any): CashClaimRow[] {
    try {
      const raw = sessionStorage.getItem(this.cashCacheKey(depositId));
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private saveCashClaimsToCache(): void {
    if (!this.cashDeposit) {
      return;
    }
    try {
      sessionStorage.setItem(this.cashCacheKey(this.cashDeposit.billingDepositDetailId), JSON.stringify(this.cashClaims));
    } catch {
    }
  }

  private clearCashClaimsCache(depositId: any): void {
    try {
      sessionStorage.removeItem(this.cashCacheKey(depositId));
    } catch {
    }
  }

  formatCurrency(value: number | null | undefined): string {
    const num = Number(value) || 0;
    const sign = num < 0 ? '-' : '';
    return `${sign}$${Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  // ---------------------------------------------------------------------------
  // Export modal
  // ---------------------------------------------------------------------------

  openExportModal(): void {
    this.exportTimeframe = 'today';
    this.exportReportType = '';
    this.exportError = '';
    this.onExportTimeframeChange();
    this.showExportModal = true;
  }

  closeExportModal(): void {
    this.showExportModal = false;
  }

  onExportTimeframeChange(): void {
    const today = new Date();
    const daysBack = this.exportTimeframe === 'last30' ? 30 : this.exportTimeframe === 'last180' ? 180 : 0;
    const start = new Date(today);
    start.setDate(start.getDate() - daysBack);
    this.exportStartDate = start;
    this.exportEndDate = today;
  }

  exportSubmitDisabled(): boolean {
    return !this.exportStartDate || !this.exportEndDate;
  }

  onExportSubmit(): void {
    if (this.exportSubmitDisabled() || this.exportSubmitting) {
      return;
    }

    this.exportError = '';
    this.exportSubmitting = true;
    const startDate = this.toIsoDate(this.exportStartDate!);
    const endDate = this.toIsoDate(this.exportEndDate!);

    this.billingService
      .exportDepositReport(this.programName || 'NY', startDate, endDate, this.exportReportType || undefined)
      .subscribe({
        next: (response) => this.handleExportResponse(response),
        error: (err) => {
          this.exportSubmitting = false;
          this.logger.error('Failed to export deposits', err);
          this.exportError = 'Could not export deposits. Please try again.';
        },
      });
  }

  private handleExportResponse(response: HttpResponse<Blob>): void {
    this.exportSubmitting = false;
    const blob = response.body;
    const contentType = response.headers.get('Content-Type') ?? '';

    if (!blob || contentType.includes('application/json')) {
      this.readBlobAsJson(blob).then((errorBody) => {
        this.exportError = errorBody?.responsemsg?.[0]?.desc || errorBody?.message || 'Could not export deposits. Please try again.';
      });
      return;
    }

    const filename = this.filenameFromContentDisposition(response.headers.get('Content-Disposition')) || 'deposit-export.xlsx';
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    this.closeExportModal();
  }

  private readBlobAsJson(blob: Blob | null): Promise<any> {
    if (!blob) {
      return Promise.resolve(null);
    }
    return blob.text().then((text) => {
      try {
        return JSON.parse(text);
      } catch {
        return null;
      }
    });
  }

  private filenameFromContentDisposition(header: string | null): string | null {
    if (!header) {
      return null;
    }
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
    return match ? decodeURIComponent(match[1]) : null;
  }

  private toIsoDate(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  private toMmDdYyyy(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()}`;
  }
}
